import React, { useState, useEffect, useContext, useRef } from 'react';
import Map from '../components/Map';
import DynamicIslandHeader from '../components/DynamicIslandHeader';
import BottomActionBar from '../components/BottomActionBar';
import './HomePage.scss';
import NoteFormDialog from '../components/NoteFormDialog';
import LocalNotesDialog from '../components/LocalNotesDialog';
import PocketDialog from '../components/PocketDialog';
import { AppContext } from '../AppContext';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getDocs } from 'firebase/firestore';
import { geohashQueryBounds, distanceBetween } from 'geofire-common';
import goldenNoteService from '../services/goldenNoteService';
import noteSpawnService from '../services/noteSpawnService';
import areaSpawnService from '../services/areaSpawnService';
import { getAuth } from 'firebase/auth';
import { Snackbar, Box, Typography } from '@mui/material';
import radarSound from '../assets/audio/radar.mp3';


function HomePage() {
  const { isNoteFormOpen, currentLocation, handleNoteFormOpen, handleLocationChange, isLocalNotesDialogOpen, handleLocalNotesDialogOpen} = useContext(AppContext);
  const [showResetLocation, setShowResetLocation] = useState(false);
  const [noteCount, setNoteCount] = useState(0);
  const [isPocketDialogOpen, setIsPocketDialogOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const mapRef = useRef(null);
  const radarAudioRef = useRef(null);

  // Pin placement mode states
  const [isPlacementMode, setIsPlacementMode] = useState(false);
  const [pinLocation, setPinLocation] = useState(null);
  const [placementCenter, setPlacementCenter] = useState(null); // Fixed center for placement radius
  const [pendingNoteData, setPendingNoteData] = useState(null); // {content, expiryHours, pointsCost}
  const [dialogMapCenter, setDialogMapCenter] = useState(null); // Map center when dialog opens

  // Treasure hunting mode states
  const [radarActive, setRadarActive] = useState(false);
  const [radarResult, setRadarResult] = useState(null);
  const [radarSnackbar, setRadarSnackbar] = useState({ open: false, message: '' });

  // Fetch note count when location changes or when refreshTrigger changes
  useEffect(() => {
    async function fetchNoteCount() {
      if (!currentLocation) return;

      try {
        let count = 0;
        const db = firebase.firestore();
        const notesRef = db.collection('tokens');
        const radiusInM = 100;
        const geofireLocation = [currentLocation.lat, currentLocation.lng];
        const bounds = geohashQueryBounds(geofireLocation, radiusInM);

        for (const b of bounds) {
          const q = notesRef
            .orderBy('location.geohash')
            .startAt(b[0])
            .endAt(b[1]);

          const querySnapshot = await getDocs(q);
          // Only count active notes that haven't expired
          querySnapshot.forEach((doc) => {
            const noteData = doc.data();
            // Filter out expired notes
            const expiresAt = noteData.expiresAt?.toDate();
            const isExpired = expiresAt && expiresAt < new Date();

            if (noteData.active !== false && !isExpired) {
              count++;
            }
          });
        }

        setNoteCount(count);
      } catch (error) {
        console.error('Error fetching note count:', error);
      }
    }

    fetchNoteCount();

    // Update count every 30 seconds
    const intervalId = setInterval(fetchNoteCount, 30000);
    return () => clearInterval(intervalId);
  }, [currentLocation, refreshTrigger]);

  // Check for golden note spawning when location changes
  useEffect(() => {
    async function checkGoldenNoteSpawn() {
      if (!currentLocation) return;

      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      const spawned = await goldenNoteService.checkAndSpawnGoldenNote(
        currentLocation,
        userId
      );

      if (spawned) {
        // Refresh note count to include new golden note
        // The count will update automatically via the interval above
        console.log('✨ A golden note appeared nearby!');
      }
    }

    checkGoldenNoteSpawn();
  }, [currentLocation]);

  // OLD SPAWN SYSTEM DISABLED - Using new treasure hunting system instead
  // Start note spawn system when location is available
  // useEffect(() => {
  //   if (currentLocation) {
  //     // Start the spawn system
  //     noteSpawnService.start(currentLocation, () => currentLocation);

  //     // Cleanup on unmount
  //     return () => {
  //       noteSpawnService.stop();
  //     };
  //   }
  // }, [currentLocation]);

  // Initialize area spawns for treasure hunting when user logs in
  const hasInitializedSpawns = useRef(false);
  useEffect(() => {
    async function initializeAreaSpawns() {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;

      if (!userId || !currentLocation || hasInitializedSpawns.current) return;

      try {
        hasInitializedSpawns.current = true;
        const spawnsCreated = await areaSpawnService.initializeUserArea(currentLocation);
        if (spawnsCreated > 0) {
          console.log(`✨ Initialized ${spawnsCreated} treasure spawns in the area`);
        }
      } catch (error) {
        console.error('Error initializing area spawns:', error);
        hasInitializedSpawns.current = false; // Reset on error so it can retry
      }
    }

    initializeAreaSpawns();
  }, [currentLocation]); // Run once when location is available

  const handleAddNote = () => {
    // Capture current map center when opening dialog
    if (mapRef.current && mapRef.current.getMapCenter) {
      const mapCenter = mapRef.current.getMapCenter();
      setDialogMapCenter(mapCenter);
    } else {
      setDialogMapCenter(currentLocation);
    }
    handleNoteFormOpen(true);
  };

  const handleAddWaypoint = () => {
    // TODO: Implement waypoint functionality
  };

  const handleViewLocalNotes = () => {
    handleLocalNotesDialogOpen(true);
  };

  const handleResetLocation = () => {
    if (mapRef.current && mapRef.current.getCurrentLocation) {
      mapRef.current.getCurrentLocation();
      setShowResetLocation(false);
    }
  };

  const handleMapDragged = (isDragged) => {
    setShowResetLocation(isDragged);
  };

  const handleNotificationClick = () => {
    handleLocalNotesDialogOpen(true);
  };

  const handlePocketClick = () => {
    setIsPocketDialogOpen(true);
  };

  const handleNotePickedUp = () => {
    // Trigger refresh of points and note count
    setRefreshTrigger(prev => prev + 1);
  };

  const handleProceedToPlacement = (content, expiryHours, pointsCost) => {
    // Get current map center from the map component
    let mapCenter = currentLocation;
    if (mapRef.current && mapRef.current.getMapCenter) {
      mapCenter = mapRef.current.getMapCenter();
    }

    // Determine if this is a remote drop (>100m from user)
    const distance = distanceBetween(
      [currentLocation.lat, currentLocation.lng],
      [mapCenter.lat, mapCenter.lng]
    );
    const isRemote = distance > 0.1; // 0.1km = 100m

    // If remote, center on map center; if local, center on user location
    const placementCenterLocation = isRemote ? mapCenter : currentLocation;

    // Save note data and enter placement mode
    setPendingNoteData({ content, expiryHours, pointsCost });
    setPlacementCenter(placementCenterLocation);
    setPinLocation(placementCenterLocation); // Start pin at placement center
    setIsPlacementMode(true);
  };

  const handleCancelPlacement = () => {
    // Go back to note form dialog with saved data
    setIsPlacementMode(false);
    setPlacementCenter(null);
    handleNoteFormOpen(true);
  };

  const handleConfirmPlacement = async () => {
    // Call the Map's dropNote method
    if (mapRef.current && mapRef.current.dropNote) {
      const success = await mapRef.current.dropNote();
      if (success) {
        setIsPlacementMode(false);
        setPendingNoteData(null);
        setPinLocation(null);
        setPlacementCenter(null);
        setRefreshTrigger(prev => prev + 1);
      }
    }
  };

  const handlePinLocationChange = (newLocation) => {
    setPinLocation(newLocation);
  };

  const handleRadarScan = async () => {
    if (!currentLocation) return;

    // Activate radar animation
    setRadarActive(true);

    // Play radar sound after 1 second
    setTimeout(() => {
      if (radarAudioRef.current) {
        radarAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
      }
    }, 1000);

    try {
      // Scan for items within 200m
      const nearbySpawns = await areaSpawnService.getSpawnsInRadius(currentLocation, 200);
      const itemsDetected = nearbySpawns.length > 0;

      setRadarResult({
        itemsDetected,
        count: nearbySpawns.length,
      });

      // Show result message after 5 second sweep completes
      const message = itemsDetected
        ? `Radar detected ${nearbySpawns.length} signal${nearbySpawns.length > 1 ? 's' : ''} nearby!`
        : 'No signals detected in range';

      // Clear animation after 5 seconds, then show toast
      setTimeout(() => {
        setRadarActive(false);
        setRadarSnackbar({ open: true, message });
      }, 5000);
    } catch (error) {
      console.error('Error scanning radar:', error);
      setRadarActive(false);
    }
  };

  // Note: Success dialog state management
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  return (
    <div className="page-container">
      <DynamicIslandHeader
        noteCount={noteCount}
        onNotificationClick={handleNotificationClick}
        onPocketClick={handlePocketClick}
        refreshTrigger={refreshTrigger}
      />
      <Map
        ref={mapRef}
        onLocationChange={handleLocationChange}
        onDragged={handleMapDragged}
        onNotePickedUp={handleNotePickedUp}
        isPlacementMode={isPlacementMode}
        pinLocation={pinLocation}
        placementCenter={placementCenter}
        onPinLocationChange={handlePinLocationChange}
        pendingNoteData={pendingNoteData}
        onConfirmPlacement={handleConfirmPlacement}
        radarActive={radarActive}
        radarResult={radarResult}
      />
      {!isPlacementMode && (
        <BottomActionBar
          onAddNote={handleAddNote}
          onAddWaypoint={handleAddWaypoint}
          onResetLocation={handleResetLocation}
          showResetLocation={showResetLocation}
          onRadarScan={handleRadarScan}
        />
      )}
      {isPlacementMode && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '20px',
          background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.95) 100%)',
          display: 'flex',
          gap: '12px',
          zIndex: 1000,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
        }}>
          <button
            onClick={handleCancelPlacement}
            style={{
              flex: 1,
              padding: '16px',
              fontSize: '16px',
              fontFamily: 'Gochi Hand, cursive',
              fontWeight: 600,
              border: '2px solid #2c2c2c',
              backgroundColor: 'white',
              color: '#2c2c2c',
              borderRadius: '12px',
              cursor: 'pointer',
            }}
          >
            CANCEL
          </button>
          <button
            onClick={handleConfirmPlacement}
            style={{
              flex: 2,
              padding: '16px',
              fontSize: '18px',
              fontFamily: 'Gochi Hand, cursive',
              fontWeight: 600,
              border: 'none',
              backgroundColor: '#2c2c2c',
              color: 'white',
              borderRadius: '12px',
              cursor: 'pointer',
            }}
          >
            DROP HERE (-{pendingNoteData?.pointsCost || 0} pts)
          </button>
        </div>
      )}
      <NoteFormDialog
        open={isNoteFormOpen}
        handleClose={() => handleNoteFormOpen(false)}
        onProceedToPlacement={handleProceedToPlacement}
        initialContent={pendingNoteData?.content || ''}
        initialExpiryHours={pendingNoteData?.expiryHours || 1}
        userLocation={currentLocation}
        mapCenter={dialogMapCenter || currentLocation}
        isMapDragged={showResetLocation}
      />
      <LocalNotesDialog
        open={isLocalNotesDialogOpen}
        handleClose={() => handleLocalNotesDialogOpen(false)}
        currentLocation={currentLocation}
        onNotePickedUp={handleNotePickedUp}
      />
      <PocketDialog
        open={isPocketDialogOpen}
        onClose={() => setIsPocketDialogOpen(false)}
        refreshTrigger={refreshTrigger}
      />
      {/* Radar Result Snackbar */}
      <Snackbar
        open={radarSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setRadarSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '80px !important',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            color: '#4CAF50',
            padding: '16px 28px',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            border: '2px solid rgba(76, 175, 80, 0.3)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: 'Gochi Hand, cursive',
            }}
          >
            {radarSnackbar.message}
          </Typography>
        </Box>
      </Snackbar>

      {/* Hidden audio element */}
      <audio ref={radarAudioRef} src={radarSound} preload="auto" />
    </div>
  );
}

export default HomePage;
